import { Button, Combobox, Input, NumberInput } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn, levenshteinDistance } from "@tellescope/utils";
import { MedicationResponse } from "@tellescope/types-models";
import { UNKNOWN_DRUG_NAME } from "../../utils";
import { useMedicationCatalog } from "./useMedicationCatalog";

export interface AddMedicationFormProps {
  medication?: MedicationResponse;
  onChange: (medication: MedicationResponse) => void;
  onRemove?: () => void;
  className?: string;
  dataSource?: string;
  autoFocus?: boolean;
}

export const AddMedicationForm = ({
  medication: providedMedication,
  onChange,
  onRemove,
  className,
  dataSource,
  autoFocus,
}: AddMedicationFormProps) => {
  const { t } = useTranslation("forms");
  const storedMedication: MedicationResponse = {
    displayTerm: providedMedication?.displayTerm ?? "",
    drugName: providedMedication?.drugName ?? "",
    otherDrug: providedMedication?.otherDrug,
    drugSynonym: providedMedication?.drugSynonym,
    rxNormCode: providedMedication?.rxNormCode,
    NDCs: providedMedication?.NDCs,
    dosage: providedMedication?.dosage ?? undefined,
    reasonForTaking: providedMedication?.reasonForTaking,
  };

  const {
    isCanvas,
    searchQuery,
    setSearchQuery,
    allDisplayNames,
    specificDrugOptions,
    rxNormCode,
    ndcCodes,
    isLoadingSpecificDrugs,
    canvasOptions,
  } = useMedicationCatalog({
    selectedDisplayName: storedMedication.displayTerm,
    selectedSpecificDrugName: storedMedication.drugName,
    dataSource,
  });

  const isDrugUnresolvable =
    !isCanvas && !!storedMedication.displayTerm && !isLoadingSpecificDrugs && !specificDrugOptions.length;

  const medication: MedicationResponse = storedMedication.drugName
    ? {
        ...storedMedication,
        rxNormCode: rxNormCode?.[0] ?? storedMedication.rxNormCode,
        NDCs: ndcCodes?.length ? ndcCodes.map(String) : storedMedication.NDCs,
      }
    : isDrugUnresolvable
    ? { ...storedMedication, drugName: UNKNOWN_DRUG_NAME }
    : storedMedication;

  const selectedSpecificDrug = useMemo(() => {
    return specificDrugOptions.find((drug) => drug.name === medication.drugName);
  }, [medication.drugName, specificDrugOptions]);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Combobox
        modal
        value={medication.displayTerm ? { value: medication.displayTerm, label: medication.displayTerm } : undefined}
        onChange={(option) => {
          const displayTerm = option?.value ?? "";
          if (!isCanvas) return onChange({ ...medication, displayTerm });
          const selected = canvasOptions?.find((canvasOption) => canvasOption.display === displayTerm);
          onChange({ ...medication, displayTerm, drugName: displayTerm, fdbCode: selected?.code });
        }}
        {...(isCanvas ? { searchQuery, setSearchQuery } : {})}
        options={allDisplayNames.map((displayName) => ({
          value: displayName,
          label: displayName,
        }))}
        alwaysFilter
        customFilter={(searchTerm, options) => {
          if (isCanvas) return options;
          if (searchTerm) {
            return [...options]
              .filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
              .sort((a, b) => a.label.length - b.label.length)
              .slice(0, 50);
          }
          return options.slice(0, 50);
        }}
        placeholder={t("Choose a medication...")}
        searchPlaceholder={t("Search medications...")}
        autoFocus={autoFocus}
        isLoading={isCanvas ? isLoadingSpecificDrugs : allDisplayNames.length === 0}
        loadingText={t("Loading medications...")}
        emptyText={t("No medications found")}
        clearable
      />
      {!!medication.displayTerm && !medication.otherDrug && !!specificDrugOptions.length && (
        <Combobox
          modal
          value={
            medication.drugName
              ? {
                  value: medication.drugName,
                  label: selectedSpecificDrug?.synonym ?? medication.drugName,
                }
              : undefined
          }
          onChange={(option) => {
            const selected = specificDrugOptions.find((drug) => drug.name === option?.value);
            onChange({
              ...medication,
              drugName: option?.value ?? "",
              drugSynonym: selected?.synonym,
              rxNormCode: selected ? String(selected.rxcui) : undefined,
            });
          }}
          options={specificDrugOptions.map((drug) => ({
            value: drug.name,
            label: drug.synonym,
          }))}
          alwaysFilter
          customFilter={(searchTerm, options) => {
            const getSearchableString = (option: (typeof options)[number]): string => {
              return `${option.label}|${option.value}|${option.description ?? ""}`.toLowerCase();
            };

            if (searchTerm) {
              return [...options]
                .filter((option) => getSearchableString(option).includes(searchTerm.toLowerCase()))
                .sort(
                  (a, b) =>
                    levenshteinDistance(getSearchableString(b), searchTerm.toLowerCase()) -
                    levenshteinDistance(getSearchableString(a), searchTerm.toLowerCase())
                );
            }
            return [...options].slice(0, 50);
          }}
          placeholder={t("Drug Select")}
          searchPlaceholder={t("Search specific drugs...")}
          emptyText={t("No specific drugs found")}
          clearable
        />
      )}
      {!!medication.displayTerm && (
        <>
          {(isDrugUnresolvable || !!medication.otherDrug) && (
            <Input
              value={medication.otherDrug}
              onChange={(e) => onChange({ ...medication, otherDrug: e.target.value })}
              placeholder={t("Other Drug")}
            />
          )}
          <div className="flex gap-2">
            <NumberInput
              value={medication.dosage?.quantity ? parseInt(medication.dosage.quantity) : undefined}
              onChange={(quantity) =>
                onChange({
                  ...medication,
                  dosage: {
                    ...medication.dosage,
                    quantity: String(quantity),
                    value: medication.dosage?.value ?? "",
                    unit: medication.dosage?.unit ?? "",
                  },
                })
              }
              placeholder={t("Units (e.g. capsule, table, puff) per dose?")}
              className="flex-1 w-full"
            />
            <Select
              value={medication.dosage?.frequency}
              onValueChange={(frequency) =>
                onChange({
                  ...medication,
                  dosage: {
                    ...medication.dosage,
                    frequency,
                    value: medication.dosage?.value ?? "",
                    unit: medication.dosage?.unit ?? "",
                  },
                })
              }
            >
              <Select.Trigger className="flex-1 w-full">
                {medication.dosage?.frequency ? <Select.Value /> : t("How many times per day?")}
              </Select.Trigger>
              <Select.Content>
                <Select.Item value="1">{t("1")}</Select.Item>
                <Select.Item value="2">{t("2")}</Select.Item>
                <Select.Item value="3">{t("3")}</Select.Item>
                <Select.Item value="4">{t("4")}</Select.Item>
                <Select.Item value="5">{t("5")}</Select.Item>
                <Select.Item value="6">{t("6")}</Select.Item>
                <Select.Item value="As Needed">{t("As Needed")}</Select.Item>
              </Select.Content>
            </Select>
          </div>
          <Input
            value={medication.reasonForTaking}
            onChange={(e) =>
              onChange({
                ...medication,
                reasonForTaking: e.target.value,
              })
            }
            placeholder={t("Reason for taking medication")}
          />
          {!!onRemove && (
            <div className="w-full flex justify-end">
              <Button variant="outline" onClick={onRemove}>
                {t("Remove Medication")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
